import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { SessionUser } from '@vision360/contracts';
import { Bell, BellRing, Database, LayoutDashboard, LogOut, Search, Settings } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { VersionFooter } from '@/components/version-footer';
import { SESSION_KEY } from '@/features/auth/session-query';
import { AutostartPrompt } from '@/features/autostart/autostart';
import { LiveStreamProvider } from '@/features/live/live-stream';
import { Toaster } from '@/features/notifications/toaster';
import { useSession } from '@/lib/auth/session-context';
import { CommandPalette } from '@/palette/palette';
import { usePaletteStore } from '@/stores/palette';
import { useAlerts } from '@/widgets/_sdk/hooks';

const MAIN_ID = 'main-content';
/** The nav badge reads one page of open alerts; a full page shows as "50+". */
const BADGE_LIMIT = 50;

function AlertsNavLink() {
  const { t } = useTranslation('alerts');
  const open = useAlerts({ status: 'open', limit: BADGE_LIMIT });
  const count = open.data?.alerts.length ?? 0;
  const full = count >= BADGE_LIMIT;
  return (
    <Link
      to="/alerts"
      className="inline-flex items-center gap-2 text-sm hover:underline [&.active]:font-semibold"
    >
      <Bell aria-hidden="true" className="h-4 w-4" />
      {t('nav')}
      {count > 0 && (
        <span
          key={count}
          className="animate-enter rounded-full bg-primary px-1.5 font-mono text-xs leading-5 font-medium text-primary-foreground tabular-nums"
        >
          <span aria-hidden="true">{full ? `${BADGE_LIMIT}+` : count}</span>
          <span className="sr-only">
            {full ? t('navOpenMany', { count: BADGE_LIMIT }) : t('navOpen', { count })}
          </span>
        </span>
      )}
    </Link>
  );
}

function SearchButton() {
  const { t } = useTranslation('palette');
  const setOpen = usePaletteStore((s) => s.setOpen);
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-keyshortcuts="Control+K Meta+K"
      className="inline-flex h-8 items-center gap-2 rounded-md border bg-muted/40 pl-2.5 pr-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <Search aria-hidden="true" className="h-4 w-4" />
      <span className="sr-only sm:not-sr-only">{t('open')}</span>
      <kbd className="ml-2 hidden rounded-sm border bg-background px-1.5 font-mono text-[0.6875rem] leading-5 sm:inline">
        {t('shortcut')}
      </kbd>
    </button>
  );
}

/** Layout for signed-in pages: skip link, header with sign-out, main landmark, version footer. */
export function AppShell({ user, children }: { user: SessionUser; children: ReactNode }) {
  const { t } = useTranslation([
    'common',
    'auth',
    'home',
    'settings',
    'dashboards',
    'sources',
    'rules',
  ]);
  const session = useSession();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: () => session.logout(),
    // Signing out locally never depends on the server answering.
    onSettled: () => {
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] });
      queryClient.setQueryData(SESSION_KEY, null);
    },
  });

  return (
    <LiveStreamProvider>
      <div className="flex min-h-screen flex-col">
        <a
          href={`#${MAIN_ID}`}
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2"
        >
          {t('common:a11y.skipToContent')}
        </a>
        <header className="flex items-center justify-between border-b px-6 py-3">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-semibold tracking-widest text-primary">
              {t('common:app.name')}
            </Link>
            <SearchButton />
          </div>
          <nav className="flex items-center gap-4">
            <Link
              to="/"
              activeOptions={{ exact: true }}
              className="inline-flex items-center gap-2 text-sm hover:underline [&.active]:font-semibold"
            >
              <LayoutDashboard aria-hidden="true" className="h-4 w-4" />
              {t('dashboards:nav')}
            </Link>
            <Link
              to="/sources"
              className="inline-flex items-center gap-2 text-sm hover:underline [&.active]:font-semibold"
            >
              <Database aria-hidden="true" className="h-4 w-4" />
              {t('sources:nav')}
            </Link>
            <AlertsNavLink />
            <Link
              to="/rules"
              className="inline-flex items-center gap-2 text-sm hover:underline [&.active]:font-semibold"
            >
              <BellRing aria-hidden="true" className="h-4 w-4" />
              {t('rules:nav')}
            </Link>
            <Link
              to="/settings"
              className="inline-flex items-center gap-2 text-sm hover:underline [&.active]:font-semibold"
            >
              <Settings aria-hidden="true" className="h-4 w-4" />
              {t('settings:nav')}
            </Link>
            <span className="text-sm text-muted-foreground">
              {t('home:signedInAs', { username: user.username })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              {logout.isPending ? <Spinner /> : <LogOut aria-hidden="true" className="h-4 w-4" />}
              {logout.isPending ? t('auth:logout.pending') : t('auth:logout.action')}
            </Button>
          </nav>
        </header>
        <main id={MAIN_ID} tabIndex={-1} className="flex-1 p-6 focus:outline-hidden">
          <AutostartPrompt />
          {children}
        </main>
        <VersionFooter />
        <Toaster />
        <CommandPalette />
      </div>
    </LiveStreamProvider>
  );
}
